import { getFeatureFlag, isBoolean } from "@sinch/feature-flags";
import { useEffect, useMemo } from "react";
import type { ShellUser } from "../api/types";
import { useUser } from "../user-context";
import { useLocalStorage } from "../utils";
import {
  INBOX_LOCAL_STORAGE_CONFIG_KEY,
  INBOX_LOCAL_STORAGE_SELECTED_USER_IDS_KEY,
  INBOX_V2_VERSION,
  INBOX_V4_VERSION,
} from "./useInboxConfig.constants";

export type InboxVersion = typeof INBOX_V2_VERSION | typeof INBOX_V4_VERSION;

export type InboxConfig = {
  inboxVersion: InboxVersion;
};

type UserPreferences = {
  preferences: {
    inboxVersion: InboxVersion;
  };
};

type LocalStorageInboxConfig = {
  userPreferences?: {
    [name: string]: UserPreferences;
  };
};

export const useInboxConfig = () => {
  const [userIdsInV4] = useLocalStorage(
    INBOX_LOCAL_STORAGE_SELECTED_USER_IDS_KEY,
    (x: unknown): x is string[] => typeof x === "object",
    []
  );

  const [inboxLocalStorageConfig, setInboxLocalStorageConfig] = useLocalStorage(
    INBOX_LOCAL_STORAGE_CONFIG_KEY,
    (x: unknown): x is LocalStorageInboxConfig => typeof x === "object",
    {
      userPreferences: {},
    }
  );

  useMigratedInboxConfig(
    userIdsInV4,
    inboxLocalStorageConfig,
    setInboxLocalStorageConfig
  );
  return useRetrieveInboxConfig(inboxLocalStorageConfig);
};

const useMigratedInboxConfig = (
  userIdsInV4: string[],
  inboxLocalStorageConfig: LocalStorageInboxConfig,
  setInboxLocalStorageConfig: (newValue: LocalStorageInboxConfig) => void
) => {
  useEffect(() => {
    if (userIdsInV4.length === 0) {
      return;
    }

    setInboxLocalStorageConfig({
      ...inboxLocalStorageConfig,
      userPreferences: {
        ...inboxLocalStorageConfig.userPreferences,
        ...userIdsInV4.reduce((acc, userId) => {
          acc[userId] = { preferences: { inboxVersion: INBOX_V4_VERSION } };
          return acc;
        }, {} as { [name: string]: UserPreferences }),
      },
    });

    window.localStorage.removeItem(INBOX_LOCAL_STORAGE_SELECTED_USER_IDS_KEY);
    // Should be called only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const getInboxLocalStoragePreference = (
  inboxLocalStorageConfig: LocalStorageInboxConfig,
  user: ShellUser
) =>
  inboxLocalStorageConfig.userPreferences?.[user.id]?.preferences?.inboxVersion;

const useRetrieveInboxConfig: (
  inboxLocalStorageConfig: LocalStorageInboxConfig
) => InboxConfig = (inboxLocalStorageConfig: LocalStorageInboxConfig) => {
  const isLegacyVersionForbbidenFF = getFeatureFlag(
    "inbox_forbid_legacy_version_flag",
    isBoolean,
    false
  );

  const user = useUser();

  const inboxVersion = useMemo(() => {
    if (!isLegacyVersionForbbidenFF) {
      const cohortVersion = getFeatureFlag("migrated_to_v4", isBoolean, false)
        ? INBOX_V4_VERSION
        : INBOX_V2_VERSION;
      return (
        getInboxLocalStoragePreference(inboxLocalStorageConfig, user) ??
        cohortVersion
      );
    }

    const amsLegacyInboxFeature =
      user.rights.indexOf("inbox.legacy-setup") >= 0;
    if (amsLegacyInboxFeature) {
      return (
        getInboxLocalStoragePreference(inboxLocalStorageConfig, user) ??
        INBOX_V2_VERSION
      );
    }

    return INBOX_V4_VERSION;
  }, [isLegacyVersionForbbidenFF, inboxLocalStorageConfig, user]);

  return {
    inboxVersion,
  };
};
